import React, { useMemo } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Info, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/core/utils';

interface PayrollStatusAlertsProps {
  // From month status
  monthWarnings: string[];

  // Slovak compliance
  effectiveCountryCode: string | null;
  needingSetupCount: number;

  // Missing payroll records
  totalEmployees: number;
  employeesWithRecords: number;

  // Optional: planning errors
  planningError?: string | null;

  // Failed payments
  failedPaymentsCount?: number;
  onViewFailedPayments?: () => void;
}

type AlertSeverity = 'error' | 'warning' | 'info';

interface AlertItem {
  id: string;
  severity: AlertSeverity;
  message: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

const SEVERITY_CONFIG: Record<AlertSeverity, {
  icon: React.ElementType;
  containerClass: string;
  iconClass: string;
}> = {
  error: {
    icon: AlertCircle,
    containerClass: 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20',
    iconClass: 'text-red-600 dark:text-red-400',
  },
  warning: {
    icon: AlertTriangle,
    containerClass: 'border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20',
    iconClass: 'text-orange-600 dark:text-orange-400',
  },
  info: {
    icon: Info,
    containerClass: 'border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20',
    iconClass: 'text-blue-600 dark:text-blue-400',
  },
};

export const PayrollStatusAlerts: React.FC<PayrollStatusAlertsProps> = ({
  monthWarnings,
  effectiveCountryCode,
  needingSetupCount,
  totalEmployees,
  employeesWithRecords,
  planningError,
  failedPaymentsCount = 0,
  onViewFailedPayments,
}) => {
  // Build list of all alerts
  const alerts = useMemo(() => {
    const items: AlertItem[] = [];

    // Planning errors (highest priority)
    if (planningError) {
      items.push({
        id: 'planning-error',
        severity: 'error',
        message: 'Failed to load planning data',
        description: planningError,
      });
    }

    // Failed payments (error level)
    if (failedPaymentsCount > 0) {
      items.push({
        id: 'failed-payments',
        severity: 'error',
        message: `${failedPaymentsCount} payment${failedPaymentsCount !== 1 ? 's' : ''} failed and need${failedPaymentsCount === 1 ? 's' : ''} attention`,
        action: onViewFailedPayments ? {
          label: 'View Failed',
          onClick: onViewFailedPayments,
        } : undefined,
      });
    }

    // Slovak compliance warnings
    if (effectiveCountryCode === 'SK' && needingSetupCount > 0) {
      items.push({
        id: 'slovak-compliance',
        severity: 'warning',
        message: `${needingSetupCount} employee${needingSetupCount > 1 ? 's' : ''} need Slovak compliance setup`,
        description: 'Go to Employee Management to configure tax and insurance details.',
      });
    }

    // Month status warnings
    monthWarnings.forEach((warning, index) => {
      items.push({
        id: `month-warning-${index}`,
        severity: 'warning',
        message: warning,
      });
    });

    // Missing payroll records (info level)
    const employeesWithoutRecords = totalEmployees - employeesWithRecords;
    if (totalEmployees > 0 && employeesWithoutRecords > 0) {
      items.push({
        id: 'missing-records',
        severity: 'info',
        message: `${employeesWithoutRecords} employee${employeesWithoutRecords > 1 ? 's' : ''} shown without payroll records`,
        description: 'Payroll will be automatically calculated when they log their time entries.',
      });
    }

    // Sort by severity: error -> warning -> info
    const severityOrder: Record<AlertSeverity, number> = { error: 0, warning: 1, info: 2 };
    return items.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  }, [monthWarnings, effectiveCountryCode, needingSetupCount, totalEmployees, employeesWithRecords, planningError, failedPaymentsCount, onViewFailedPayments]);

  // Don't render anything if no alerts
  if (alerts.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      {alerts.map((alert) => {
        const config = SEVERITY_CONFIG[alert.severity];
        const Icon = config.icon;

        return (
          <Alert
            key={alert.id}
            className={cn('border', config.containerClass)}
          >
            <Icon className={cn('h-4 w-4', config.iconClass)} />
            <AlertDescription className="ml-2 flex items-center justify-between flex-1">
              <div>
                <span className="font-medium">{alert.message}</span>
                {alert.description && (
                  <span className="text-muted-foreground ml-1">
                    {alert.description}
                  </span>
                )}
              </div>
              {alert.action && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={alert.action.onClick}
                  className={cn('h-6 text-xs ml-2 shrink-0', config.iconClass)}
                >
                  {alert.action.label}
                </Button>
              )}
            </AlertDescription>
          </Alert>
        );
      })}
    </div>
  );
};

export default PayrollStatusAlerts;
